<template>
  <b-modal
    ref="modal"
    :visible="true"
    static
    lazy
    centered
    no-fade
    size="lg"
    :title="title"
    hide-footer
    :no-close-on-backdrop="false"
    :no-close-on-esc="false"
    @hide="onHide"
    @hidden="onHidden"
  >
    <form @submit.prevent="submit" novalidate>
      <div class="form-group">
        <label class="small font-weight-bold text-muted">Сервисный объект</label>
        <input type="text" class="form-control form-control-sm" :value="objectLabel" readonly>
      </div>

      <div class="form-row">
        <div class="form-group col-md-7">
          <label class="small font-weight-bold text-muted" for="tf-assignee">Исполнитель <span class="text-danger">*</span></label>
          <select id="tf-assignee" v-model="form.assignee_id" class="form-control form-control-sm" :disabled="busy">
            <option value="">— выберите —</option>
            <option v-for="e in engineerOptions" :key="e.id" :value="e.id">{{ e.label }}</option>
          </select>
        </div>
        <div class="form-group col-md-5">
          <label class="small font-weight-bold text-muted">Дедлайн <span class="text-danger">*</span></label>
          <date-picker
            v-model="form.deadline_date"
            type="date"
            format="DD.MM.YYYY"
            value-type="YYYY-MM-DD"
            :clearable="false"
            :editable="false"
            :disabled="busy"
            input-class="form-control form-control-sm"
            placeholder="дд.мм.гггг"
          />
        </div>
      </div>

      <div class="form-group">
        <label class="small font-weight-bold text-muted">Перечень работ</label>
        <div class="tfm__works">
          <label v-for="wt in workTypeOptions" :key="wt.id" class="tfm__work" :class="{ 'tfm__work--missing': wt.missing }">
            <input type="checkbox" :value="wt.id" v-model="form.work_type_ids" :disabled="busy"> {{ wt.label }}
          </label>
          <div v-if="workTypeOptions.length === 0" class="text-muted small">Виды работ не найдены</div>
        </div>
      </div>

      <div class="form-group">
        <label class="small font-weight-bold text-muted" for="tf-desc">Комментарий диспетчера</label>
        <textarea id="tf-desc" v-model="form.description" rows="3" class="form-control form-control-sm" :disabled="busy"></textarea>
      </div>

      <div class="form-group">
        <label class="small font-weight-bold text-muted" for="tf-exp">Прочие расходы, ₽</label>
        <input id="tf-exp" v-model="form.expenses" type="number" min="0" step="0.01" class="form-control form-control-sm" :disabled="busy" placeholder="необязательно">
      </div>

      <ul v-if="errors.length" class="alert alert-danger py-2 pl-4 mb-3" role="alert">
        <li v-for="(e, i) in errors" :key="i">{{ e }}</li>
      </ul>

      <div class="d-flex justify-content-between">
        <button type="button" class="btn btn-secondary btn-sm tfm__cancel" :disabled="busy" @click="requestCancel">Отмена</button>
        <button type="submit" class="btn btn-success btn-sm tfm__submit" :disabled="busy">
          <span v-if="busy" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>{{ saveLabel }}
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import * as api from '@/components/management/dispatcher/api';
import {
  emptyTaskForm, formFromTask, buildCreatePayload, buildUpdatePayload, validateTaskForm,
  mergeEngineerOptions, mergeWorkTypeOptions, DRAFT_CLOSE_TEXT, DRAFT_CLOSE_OK, NO_WORKS_CONFIRM_TEXT, NO_WORKS_CONFIRM_OK,
} from '@/components/management/dispatcher/taskPayload';
import { todayIso } from '@/components/management/dispatcher/format';
import { notifySuccess, notifyError } from '@/components/management/dispatcher/notify';
import modalCloseMixin, { APP_TRIGGER } from './modalCloseMixin';

export { APP_TRIGGER } from './modalCloseMixin';

export default {
  name: 'TaskFormModal',
  components: { DatePicker },
  mixins: [modalCloseMixin],
  props: {
    mode: { type: String, required: true, validator: (v) => ['create', 'edit'].includes(v) },
    location: { type: Object, required: true },
    task: { type: Object, default: null },
    draftId: { type: Number, default: null },
    engineers: { type: Array, default: () => [] },
    workTypes: { type: Array, default: () => [] },
  },
  data() {
    return {
      form: this.mode === 'edit' ? formFromTask(this.task) : emptyTaskForm(),
      errors: [],
      busy: false,
      confirming: false,
    };
  },
  computed: {
    isCreate() { return this.mode === 'create'; },
    title() { return this.isCreate ? 'Создание задачи' : `Редактирование задачи T‑${this.task ? this.task.id : ''}`; },
    saveLabel() { return this.isCreate ? 'Создать и назначить' : 'Сохранить изменения'; },
    objectLabel() { return `${this.location.name || '—'} · ${this.location.address || 'Адрес не указан'}`; },
    engineerOptions() { return mergeEngineerOptions(this.engineers, this.isCreate ? null : this.task); },
    workTypeOptions() { return mergeWorkTypeOptions(this.workTypes, this.isCreate ? null : this.task); },
  },
  methods: {
    /* ---------- closing ---------- */
    onHide(bvEvent) {
      if (!this.isCreate) return;
      if (bvEvent && bvEvent.trigger === APP_TRIGGER) return;
      if (bvEvent && typeof bvEvent.preventDefault === 'function') bvEvent.preventDefault();
      this.requestClose();
    },
    requestCancel() {
      if (this.isCreate) this.requestClose();
      else this.hideModal();
    },
    async requestClose() {
      if (this.busy) return;
      let ok = false;
      try {
        // Bootstrap-Vue rejects the MsgBox promise when its parent is destroyed before it settles
        // («MsgBox destroyed before resolve»), e.g. on a route change — that is not a confirmation.
        ok = await this.$bvModal.msgBoxConfirm(DRAFT_CLOSE_TEXT, { okTitle: DRAFT_CLOSE_OK, cancelTitle: 'Отмена', centered: true });
      } catch (e) {
        return;
      }
      if (!ok) return;
      this.busy = true;
      try {
        await api.rejectTaskDraft(this.draftId);
        this.hideModal();
      } catch (e) {
        notifyError(this, e);
      } finally {
        this.busy = false;
      }
    },
    /* ---------- saving ---------- */
    /** Works are optional, but saving a task without any is asked about first (spec §10.3, amended 2026-10-06). */
    async confirmNoWorks() {
      if (Array.isArray(this.form.work_type_ids) && this.form.work_type_ids.length > 0) return true;
      try {
        // A MsgBox destroyed before it settles rejects — that is not a confirmation (see requestClose).
        return await this.$bvModal.msgBoxConfirm(NO_WORKS_CONFIRM_TEXT, { okTitle: NO_WORKS_CONFIRM_OK, cancelTitle: 'Отмена', centered: true });
      } catch (e) {
        return false;
      }
    },
    async submit() {
      if (this.busy || this.confirming) return;
      this.errors = validateTaskForm(this.form, { mode: this.mode, today: todayIso() });
      if (this.errors.length) return;
      // The confirm box is awaited outside `busy` (the form stays enabled behind it), so a
      // second submit in that window is held off by `confirming` and re-checked after.
      this.confirming = true;
      let ok = false;
      try { ok = await this.confirmNoWorks(); } finally { this.confirming = false; }
      if (!ok || this.busy) return;
      this.busy = true;
      try {
        if (this.isCreate) {
          const payload = buildCreatePayload(this.form, this.location, this.draftId);
          try {
            const saved = await api.confirmTask(payload);
            this.finishSaved(saved, `Задача T‑${(saved && saved.id) || this.draftId} создана и назначена`);
          } catch (err) {
            await this.reconcileConfirm(err);
          }
        } else {
          const saved = await api.updateTask(buildUpdatePayload(this.form, this.location, this.task.id));
          this.finishSaved(saved, `Задача T‑${this.task.id} обновлена`);
        }
      } catch (err) {
        notifyError(this, err);
      } finally {
        this.busy = false;
      }
    },
    /** After any confirm failure ask the server whether the draft was actually assigned (spec §10.3). */
    async reconcileConfirm(err) {
      try {
        const current = await api.getTask(this.draftId);
        if (current && current.status && current.status.code !== 'blank') {
          this.finishSaved(current, `Задача T‑${current.id} создана и назначена`);
          return;
        }
      } catch (e) {
        // fall through: report the original error
      }
      notifyError(this, err, 'Не удалось подтвердить задачу — повторите');
    },
    finishSaved(task, text) {
      notifySuccess(this, text);
      this.$emit('saved', task);
      this.hideModal();
    },
  },
};
</script>

<style scoped lang="scss">
.tfm__works { border: 1px solid #e3e5e1; border-radius: 8px; max-height: 220px; overflow: auto; padding: 4px 0; }
.tfm__work { display: flex; align-items: center; gap: 8px; margin: 0; padding: 6px 12px; cursor: pointer; font-size: 13px; &:hover { background: #f7fbf9; } }
.tfm__work--missing { color: #8b918c; font-style: italic; }
</style>
